import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { supabase } from '../lib/supabaseClient';
import { sleep } from '../lib/utils';
import { SupabaseClient } from '@supabase/supabase-js';
import { ImpersonationService } from '../services/impersonationService';
import { authStateManager } from '../lib/authStateManager';

const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

interface BusinessHours {
  enabled: boolean;
  open: string;
  close: string;
  closed: boolean;
}

interface WeeklySchedule {
  monday: BusinessHours;
  tuesday: BusinessHours;
  wednesday: BusinessHours;
  thursday: BusinessHours;
  friday: BusinessHours;
  saturday: BusinessHours;
  sunday: BusinessHours;
}

interface BookingSettings {
  allow_online_bookings: boolean;
  require_deposit: boolean;
  send_confirmations: boolean;
  buffer_time: number;
  max_bookings_per_day: number | null;
  cancellation_policy: string;
  deposit_amount: number;
  deposit_type: 'percentage' | 'fixed';
  navbar_color?: string;
  logo_url?: string;
  logo_invert?: boolean;
}

interface NotificationSettings {
  email_notifications: boolean;
  sms_notifications: boolean;
  push_notifications: boolean;
  reminder_time: string;
  send_booking_confirmations: boolean;
  send_booking_reminders: boolean;
  send_booking_followups: boolean;
}

export interface Business {
  id: string;
  name: string;
  type: string;
  email: string;
  phone: string;
  business_domain_email: string;
  primary_email: string;
  email_verified: boolean;
  address: string;
  timezone: string;
  currency: string;
  business_hours: WeeklySchedule;
  booking_settings: BookingSettings;
  notification_settings: NotificationSettings;
  created_at: string;
  updated_at: string;
  owner_id: string;
  gst_number?: string;
  pst_number?: string;
  slug: string;
}

interface BusinessContextType {
  selectedBusiness: Business | null;
  businessId: string | null;
  setSelectedBusiness: (business: Business | null) => void;
  businesses: Business[];
  role: string;
  loading: boolean;
  error: string | null;
  updateBusiness: (id: string, updates: Partial<Business>) => Promise<void>;
  forceLoadBusinesses: () => void;
  handleSuccessfulLogin: () => void;
}

export const BusinessContext = createContext<BusinessContextType | null>(null);

export function useBusiness() {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error('useBusiness must be used within a BusinessProvider');
  }
  return context;
}

interface BusinessProviderProps {
  children: ReactNode;
  business: Business | null;
}

export function BusinessProvider({ children, business }: BusinessProviderProps) {
  const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(business);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState('admin');
  const [error, setError] = useState<string | null>(null);
  const [businessCount, setBusinessCount] = useState(0);
  const [businessId, setBusinessId] = useState<string | null>(business?.id || null);

  useEffect(() => {
    setBusinessId(selectedBusiness?.id || null);
  }, [selectedBusiness]);

  // Debug logging for state changes
  useEffect(() => {
    console.log('🏢 BusinessContext State:', {
      hasSelectedBusiness: !!selectedBusiness,
      businessName: selectedBusiness?.name,
      businessCount,
      loading,
      role,
      error
    });
  }, [selectedBusiness, businessCount, loading, role, error]);

  const loadBusinesses = async (retryCount = 0, mounted = true) => {
    // TEMPORARILY DISABLED: Skip loading during tab switches (check if page just became visible)
    // if (!retryCount && document.hidden) {
    //   console.log('⏭️ Skipping business load - page is hidden (tab switch)');
    //   return;
    // }

    if (!retryCount) {
      setLoading(true);
      setError(null);
    }

    if (!supabase) {
      console.error('Supabase client not initialized');
      setError('Database connection not available');
      setLoading(false);
      return;
    }

    try {
      // First check if we have a valid session
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      console.log('Auth session check:', { 
        hasSession: !!session, 
        userId: session?.user?.id,
        userMetadata: session?.user?.user_metadata,
        error: sessionError
      });
      
      if (sessionError) {
        console.error('Session error:', sessionError);
        throw sessionError;
      }

      if (!session?.user) {
        console.log('No user session found, clearing business state');
        setBusinesses([]);
        setSelectedBusiness(null);
        setLoading(false);
        return;
      }

      // Check if we're impersonating someone
      const impersonationData = ImpersonationService.getImpersonationData();
      let businessId;
      
      if (impersonationData) {
        // Use the impersonated user's business ID
        businessId = impersonationData.targetBusinessId;
        console.log('🎭 Using impersonated business ID:', businessId);
      } else {
        // Get business ID from user metadata normally
        businessId =
          session.user.user_metadata.employed_business_ids ||
          session.user.user_metadata.owned_business_ids ||
          session.user.user_metadata.business_id;
      }
      
      console.log('Business ID check:', {
        employedBusinessIds: session.user.user_metadata.employed_business_ids,
        ownedBusinessIds: session.user.user_metadata.owned_business_ids,
        businessId: session.user.user_metadata.business_id,
        finalBusinessId: businessId
      });

      // If no business ID found in metadata, check if user is a resource
      if (!businessId) {
        console.log('No business ID in metadata, checking if user is a resource...');
        
        try {
          const { data: resourceData, error: resourceError } = await supabase
            .from('resources')
            .select('business_id')
            .eq('auth_user_id', session.user.id)
            .eq('status', 'active')
            .single();

          if (!resourceError && resourceData) {
            businessId = resourceData.business_id;
            console.log('Found business ID from resource table:', businessId);
          } else {
            console.error('Error getting resource business ID:', resourceError);
            
            // Try alternative query without is_active filter
            const { data: altResourceData, error: altResourceError } = await supabase
              .from('resources')
              .select('business_id')
              .eq('auth_user_id', session.user.id)
              .single();

            if (!altResourceError && altResourceData) {
              businessId = altResourceData.business_id;
              console.log('Found business ID from resource table (without active filter):', businessId);
            } else {
              console.error('Alternative resource query also failed:', altResourceError);
              
              // Final fallback: try to find resource by email
              console.log('Trying email-based resource lookup...');
              const { data: emailResourceData, error: emailResourceError } = await supabase
                .from('resources')
                .select('business_id')
                .eq('email', session.user.email)
                .single();

              if (!emailResourceError && emailResourceData) {
                businessId = emailResourceData.business_id;
                console.log('Found business ID from resource table (by email):', businessId);
              } else {
                console.error('Email-based resource query also failed:', emailResourceError);
                throw new Error('Unable to determine business context');
              }
            }
          }
        } catch (error) {
          console.error('Failed to get resource business ID:', error);
          throw new Error('Unable to determine business context');
        }
      }

      const userIsAdmin = session.user.user_metadata.owned_business_ids;
      if (userIsAdmin) {
        console.log('User is admin');
        setRole('admin');
      } else {
        console.log('Checking resource role');
        const { data: resourceRole, error: roleError } = await supabase
          .from('resources')
          .select('role, auth_user_id')
          .eq('auth_user_id', session.user.id)
          .single();

        console.log('Resource role check:', { resourceRole, error: roleError });

        if (!roleError && resourceRole) {
          setRole(resourceRole.role);
        } else {
          console.error('Error getting resource role:', roleError);
        }
      }

      // Get business data with retry
      let businessData;
      let businessError;
      for (let i = 0; i < 3; i++) {
        console.log(`Attempting to load business data (attempt ${i + 1}/3)`);
        const result = await supabase
          .from('businesses')
          .select('*')
          .eq('id', businessId)
          .order('created_at', { ascending: false });

        if (!result.error) {
          businessData = result.data;
          console.log('Business data loaded:', businessData);
          break;
        }
        businessError = result.error;
        console.error(`Business load attempt ${i + 1} failed:`, businessError);
        await sleep(RETRY_DELAY * Math.pow(2, i));
      }

      if (businessError) throw businessError;

      setBusinesses(businessData || []);
      setBusinessCount((businessData || []).length);
      if (businessData && businessData.length > 0 && !selectedBusiness) {
        console.log('Setting selected business:', businessData[0]);
        setSelectedBusiness(businessData[0]);
      }

      setError(null);
    } catch (err) {
      console.error('Error in loadBusinesses:', err);

      // Retry on network errors
      if (
        retryCount < MAX_RETRIES && 
        (err instanceof Error && err.message.includes('Failed to fetch'))
      ) {
        console.log(`Retrying business load (attempt ${retryCount + 1}/${MAX_RETRIES})...`);
        await sleep(RETRY_DELAY * Math.pow(2, retryCount));
        return loadBusinesses(retryCount + 1, mounted);
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to load businesses';
      setError(errorMessage);
      setBusinesses([]);
      setSelectedBusiness(null);
      console.log('❌ BusinessContext: Business loading failed:', errorMessage);
    } finally {
      if (mounted) {
        setLoading(false);
        console.log('✅ BusinessContext: Loading state cleared');
      }
    }
  };

  useEffect(() => {
    let mounted = true;
    const client = supabase;

    if (!client) {
      console.error('Supabase client not initialized');
      return;
    }

    // Initialize authStateManager to ensure it's working
    console.log('🔐 BusinessContext: Initializing authStateManager...');
    authStateManager.initialize();

    // Check if there's already a session and trigger business loading
    const checkInitialSession = async () => {
      try {
        console.log('🔍 BusinessContext: Checking for existing session...');
        const { session, error } = await authStateManager.getSession();
        if (!error && session?.user) {
          console.log('✅ BusinessContext: Found existing session, loading businesses...');
          loadBusinesses(0, mounted);
        } else {
          console.log('❌ BusinessContext: No existing session found');
          // Clear loading state if no session
          setLoading(false);
        }
      } catch (error) {
        console.error('❌ BusinessContext: Error checking initial session:', error);
        // Clear loading state on error
        setLoading(false);
      }
    };

    // Timeout to prevent infinite loading - reduced to 5 seconds for better UX
    const loadingTimeout = setTimeout(() => {
      console.warn('⚠️ BusinessContext: Loading timeout reached, clearing loading state');
      if (mounted) {
        setLoading(false);
        setError('Connection timeout. Please refresh the page to try again.');
      }
    }, 5000); // 5 second timeout (reduced from 10)

    // Check for pre-loaded business data from enhanced login
    const usePreloadedData = localStorage.getItem('usePreloadedBusinessData') === 'true';
    const preloadedBusinessData = localStorage.getItem('selectedBusiness');
    const preloadedBusinesses = localStorage.getItem('businesses');
    
    if (usePreloadedData && preloadedBusinessData && preloadedBusinesses) {
      try {
        const selectedBiz = JSON.parse(preloadedBusinessData);
        const bizList = JSON.parse(preloadedBusinesses);
        
        // Validate the preloaded data structure
        if (selectedBiz?.id && Array.isArray(bizList) && bizList.length > 0) {
          console.log('🚀 BusinessContext: Using pre-loaded business data for seamless login');
          setBusinesses(bizList);
          setSelectedBusiness(selectedBiz);
          setLoading(false);
          
          // Clean up the temporary data immediately
          localStorage.removeItem('selectedBusiness');
          localStorage.removeItem('businesses');
          localStorage.removeItem('usePreloadedBusinessData');
          
          // Still set up auth listener for future changes, but don't reload on mount
          const unsubscribe = authStateManager.subscribe((event, session) => {
            console.log('BusinessContext: Auth state changed (preloaded data mode)', event, session?.user?.id);
            
            // CRITICAL: Only clear businesses on actual logout, NOT token refresh
            if (event === 'SIGNED_OUT') {
              console.log('BusinessContext: User signed out, clearing businesses...');
              if (mounted) {
                setBusinesses([]);
                setSelectedBusiness(null);
                setLoading(false);
              }
            } else if (event === 'TOKEN_REFRESHED') {
              console.log('⏭️ BusinessContext: Token refresh - no action needed (preloaded data)');
              // Don't clear/reload businesses on token refresh
            }
            // Don't reload businesses on login since we already have them
          });

          return () => {
            mounted = false;
            unsubscribe();
          };
        } else {
          console.warn('BusinessContext: Invalid preloaded data structure, falling back to standard loading');
          // Clean up invalid data
          localStorage.removeItem('selectedBusiness');
          localStorage.removeItem('businesses');
          localStorage.removeItem('usePreloadedBusinessData');
        }
      } catch (error) {
        console.error('BusinessContext: Error parsing pre-loaded business data:', error);
        // Clean up corrupted data
        localStorage.removeItem('selectedBusiness');
        localStorage.removeItem('businesses');
        localStorage.removeItem('usePreloadedBusinessData');
      }
    }

    // Standard business loading for non-enhanced login users
    console.log('BusinessContext: Initializing with standard flow');
    
    console.log('🔄 BusinessContext: Setting up SAFE auth listener...');
    
    // COMPLETELY DISABLE auth listener to prevent ANY tab-switch reloads
    // Business loading will be handled by enhanced initial session check
    console.log('🚨 BusinessContext: Auth listener DISABLED - using enhanced session checking instead');
    
    // Enhanced session checking that retries multiple times
    const checkSessionWithRetry = async (attempt = 1) => {
      if (!mounted) return; // Stop if unmounted
      
      // If we already have businesses loaded, clear loading and stop
      if (businesses.length > 0) {
        console.log('✅ BusinessContext: Businesses already loaded, clearing loading state');
        setLoading(false);
        return;
      }
      
      try {
        console.log(`🔍 BusinessContext: Session check attempt ${attempt}...`);
        const { session, error } = await authStateManager.getSession();
        if (!error && session?.user) {
          console.log('✅ BusinessContext: Found session - loading businesses...');
          setLoading(true);
          setError(null);
          await loadBusinesses(0, mounted);
          return; // Success - stop retrying
        } else {
          console.log(`❌ BusinessContext: No session found on attempt ${attempt}`);
        }
      } catch (error) {
        console.log(`❌ BusinessContext: Error in session check attempt ${attempt}:`, error);
      }
      
      // Retry up to 5 times with 500ms delay (reduced from 10)
      if (attempt < 5) {
        setTimeout(() => checkSessionWithRetry(attempt + 1), 500);
      } else {
        console.log('❌ BusinessContext: Giving up after 5 attempts - clearing loading state');
        if (mounted) {
          setLoading(false);
          setError('Unable to establish business context. Please refresh the page.');
        }
      }
    };
    
    const unsubscribe = () => {
      // No cleanup needed for this approach
    };

    // Always check for session and load businesses if needed
    if (!usePreloadedData) {
      checkSessionWithRetry();
    }
    
    // Also add a fallback check after a short delay to catch cases where
    // the auth state hasn't been established yet
    setTimeout(() => {
      if (mounted && !selectedBusiness && businesses.length === 0 && !loading) {
        console.log('🔄 BusinessContext: Fallback session check - no business data loaded yet');
        checkSessionWithRetry();
      }
    }, 2000);

    return () => {
      mounted = false;
      unsubscribe();
      clearTimeout(loadingTimeout);
    };
  }, []);

  const updateBusiness = async (id: string, updates: Partial<Business>) => {
    if (!supabase) {
      throw new Error('Supabase client not initialized');
    }

    try {
      const { data, error } = await supabase
        .from('businesses')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Update local state
      setBusinesses((prev) => prev.map((b) => (b.id === id ? data : b)));
      if (selectedBusiness?.id === id) {
        setSelectedBusiness(data);
      }
    } catch (err) {
      console.error('Error updating business:', err);
      throw err;
    }
  };

  useEffect(() => {
    console.log('Business context state updated:', {
      selectedBusiness,
      businessCount,
      loading,
      error,
    });
  }, [selectedBusiness, businesses, loading, error]);

  const value = {
    selectedBusiness,
    businessId,
    setSelectedBusiness,
    businesses,
    role,
    loading,
    error,
    updateBusiness,
    // Add manual loading function for debugging
    forceLoadBusinesses: () => {
      console.log('🔄 BusinessContext: Force loading businesses...');
      setLoading(true);
      setError(null);
      loadBusinesses(0, true);
    },
    // NEW: Method for login flow to trigger business loading
    handleSuccessfulLogin: () => {
      console.log('🚀 BusinessContext: Handling successful login - loading businesses...');
      
      // Only reload if we don't already have business data
      if (!selectedBusiness || businesses.length === 0) {
        console.log('🔄 BusinessContext: No business data found, loading...');
        setLoading(true);
        setError(null);
        loadBusinesses(0, true);
      } else {
        console.log('✅ BusinessContext: Business data already loaded, skipping reload');
      }
    }
  };

  return (
    <BusinessContext.Provider value={value}>
      {children}
    </BusinessContext.Provider>
  );
}

// Export as a single default object
const BusinessModule = {
  Provider: BusinessProvider,
  useBusiness
};

export default BusinessModule;
